import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "../../../components/CRUDcomponents/Actionbar/Modals/Search/Search";
import { Print } from "../../../components/CRUDcomponents/Actionbar/Modals/Print/Print";
import DynamicTable from "../../../components/Table/IndentTable";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { useSelector } from "react-redux";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiEndpoint = `${API_BASE_URL}/popage/indent`;

const PHIndent = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });

  const navigate = useNavigate();
  const getEditFields = () => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options:
        dropdownData.projects.map((project) => ({
          value: project.id,
          label: project.name,
        })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options:
        dropdownData.types.map((type) => ({
          value: type.id,
          label: type.name,
        })) || [],
    },
  ];

  const fetchIndents = async () => {
    try {
      const apiEndpoint = `${API_BASE_URL}/adminpage/phindent`; // Using admin endpoint
      const response = await axios.get(apiEndpoint);
      const formattedIndents = response.data
        .map((indent) => ({
          ...indent,
          L1: indent.L1 ? "Approved" : "Pending",
          L2: indent.L2 ? "Approved" : "Pending",
          PO: indent.PO ? "Approved" : "Pending",
          PH: indent.PH ? "Approved" : "Pending",
          CreationDate: new Date(indent.CreationDate)
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-"), // Format as dd-mm-yyyy
        }))
        .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));
      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
    } catch (error) {
      console.error("Error fetching indents:", error);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      const dropdownDataState = {
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      };

      setDropdownData(dropdownDataState);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchIndents();
    fetchDropdownData();
  }, []);

  const handleSearch = (query) => {
    if (!query) {
      setFilteredIndents(indents);
    } else {
      const lowercaseQuery = query.toLowerCase();
      const filtered = indents.filter((indent) => {
        return (
          indent.IndentID.toString().toLowerCase().includes(lowercaseQuery) ||
          (indent.Status &&
            indent.Status.toLowerCase().includes(lowercaseQuery)) ||
          (indent.ProjectName &&
            indent.ProjectName.toLowerCase().includes(lowercaseQuery)) ||
          (indent.TypeName &&
            indent.TypeName.toLowerCase().includes(lowercaseQuery)) ||
          (indent.CreationDate &&
            indent.CreationDate.toString()
              .toLowerCase()
              .includes(lowercaseQuery))
        );
      });
      setFilteredIndents(filtered);
    }
  };

  const handleDateFilter = (filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (dateFrom && dateTo) {
      const filtered = indents.filter((indent) => {
        const creationDate = new Date(indent.CreationDate);
        return (
          creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo)
        );
      });
      setFilteredIndents(
        filtered.sort(
          (a, b) => new Date(b.CreationDate) - new Date(a.CreationDate)
        )
      );
    } else {
      setFilteredIndents(indents);
    }
  };

  const handleRedirect = (page) => {
    navigate(page);
  };

  const handleRowClick = (row) => {
    navigate(`/PHtable`, { state: { row } });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">PH Table</h3>
        </div>

        {dropdownData.users.length > 0 && dropdownData.projects.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields()}
            apiEndpoint={apiEndpoint}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={fetchIndents}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole="project head"
            addLink="/addl2Indent"
            editRoute="/PHTable" // Added editRoute prop with default value
          />
        )}

        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={[
              { key: "IndentID", label: "Indent ID" },
              { key: "CreationDate", label: "Creation Date" },
              { key: "ProjectName", label: "Project" },
              { key: "TypeName", label: "Indent Type" },
              { key: "TaskSchedule", label: "Task Scheduled" },
              { key: "Status", label: "Status" },
              { key: "ItemCount", label: "Item Count" },
            ]}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={(row) => setSelectedRow(row)}
            onDoubleClick={(row) => handleRowClick(row)}
            rowKey="IndentID"
            highlightColor="bg-green-100"
            noDataMessage="No indents found."
            userRole="admin"
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PHIndent;
